import { useEffect, useState } from "react";
import api from "./api";
import Notification from "./Notification"; // ✅ added

export default function Categories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [note, setNote] = useState({ message: "", type: "" }); // ✅ added

  const [name_en, setNameEn] = useState("");
  const [name_de, setNameDe] = useState("");
  const [position, setPosition] = useState(0);

  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ name_en: "", name_de: "", position: 0 });

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/categories");
      setRows(data);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load");
      setNote({ message: "Failed to load categories", type: "error" }); // ✅ added
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createCategory(e) {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        name_en: name_en?.trim(),
        name_de: name_de?.trim(),
        position: Number(position) || 0,
      };
      if (!payload.name_en && !payload.name_de) {
        setErr("Please enter at least English or German name.");
        return;
      }
      await api.post("/api/categories", payload);
      setNameEn("");
      setNameDe("");
      setPosition(0);
      await load();
      setNote({ message: "Category added successfully ✅", type: "success" }); // ✅ added
    } catch (e) {
      setErr(e?.response?.data?.error || "Create failed");
      setNote({ message: "Failed to add category", type: "error" }); // ✅ added
    }
  }

  function startEdit(row) {
    setEditId(row._id);
    setEdit({
      name_en: row?.nameI18n?.en ?? row?.name ?? "",
      name_de: row?.nameI18n?.de ?? row?.name ?? "",
      position: row?.position ?? 0,
    });
  }

  function cancelEdit() {
    setEditId(null);
    setEdit({ name_en: "", name_de: "", position: 0 });
  }

  async function saveEdit(id) {
    setErr("");
    try {
      const payload = {
        name_en: edit.name_en?.trim(),
        name_de: edit.name_de?.trim(),
        position: Number(edit.position) || 0,
      };
      await api.put(`/api/categories/${id}`, payload);
      cancelEdit();
      await load();
      setNote({ message: "Category updated ✏️", type: "success" }); // ✅ added
    } catch (e) {
      setErr(e?.response?.data?.error || "Update failed");
      setNote({ message: "Update failed", type: "error" }); // ✅ added
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this category?")) return;
    setErr("");
    try {
      await api.delete(`/api/categories/${id}`);
      await load();
      setNote({ message: "Category deleted 🗑️", type: "success" }); // ✅ added
    } catch (e) {
      setErr(e?.response?.data?.error || "Delete failed");
      setNote({ message: "Delete failed", type: "error" }); // ✅ added
    }
  }

  return (
    <div style={{ display: "grid", gap: 20, color: "#222" }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#d4a017" }}>
        Categories
      </h2>

      {err && <div style={{ color: "#b00020", fontSize: 13 }}>{err}</div>}

      {/* Create Form */}
      <form
        onSubmit={createCategory}
        style={{
          display: "grid",
          gap: 14,
          border: "1px solid #ddd",
          padding: 18,
          borderRadius: 10,
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ flex: "1 1 240px" }}>
            <label style={lbl}>Name (English)*</label>
            <input
              value={name_en}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="Starters"
              style={input}
            />
          </div>
          <div style={{ flex: "1 1 240px" }}>
            <label style={lbl}>Name (Deutsch)</label>
            <input
              value={name_de}
              onChange={(e) => setNameDe(e.target.value)}
              placeholder="Vorspeisen"
              style={input}
            />
          </div>
          <div style={{ flex: "0 1 120px" }}>
            <label style={lbl}>Position</label>
            <input
              type="number"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              style={input}
            />
          </div>
        </div>

        <div>
          <button style={btnPrimary}>Add Category</button>
        </div>
        <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
          * Provide English; German optional (fallback uses English).
        </p>
      </form>

      {/* List */}
      <div
        style={{
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: 10,
          background: "#fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "600px",
          }}
        >
          <thead>
            <tr style={{ background: "#f6f3ee" }}>
              <th style={th}>Position</th>
              <th style={th}>Name (EN)</th>
              <th style={th}>Name (DE)</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ padding: 12 }}>
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ padding: 12, color: "#666" }}>
                  No categories yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isEditing = editId === row._id;
                const en = row?.nameI18n?.en ?? row?.name ?? "";
                const de = row?.nameI18n?.de ?? row?.name ?? "";
                return (
                  <tr key={row._id}>
                    <td style={td}>
                      {isEditing ? (
                        <input
                          type="number"
                          value={edit.position}
                          onChange={(e) =>
                            setEdit({ ...edit, position: e.target.value })
                          }
                          style={{ ...input, width: 90 }}
                        />
                      ) : (
                        row.position ?? 0
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <input
                          value={edit.name_en}
                          onChange={(e) =>
                            setEdit({ ...edit, name_en: e.target.value })
                          }
                          style={input}
                        />
                      ) : (
                        en
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <input
                          value={edit.name_de}
                          onChange={(e) =>
                            setEdit({ ...edit, name_de: e.target.value })
                          }
                          style={input}
                        />
                      ) : (
                        de
                      )}
                    </td>
                    <td style={{ ...td, minWidth: 150 }}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            onClick={() => saveEdit(row._id)}
                            style={btnPrimary}
                            type="button"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={btnGhost}
                            type="button"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            onClick={() => startEdit(row)}
                            style={btnPrimary}
                            type="button"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => remove(row._id)}
                            style={btnDanger}
                            type="button"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Notification */}
      <Notification
        message={note.message}
        type={note.type}
        onClose={() => setNote({ message: "", type: "" })}
      />
    </div>
  );
}

/* ========= Styles ========= */
const lbl = { fontSize: 12, fontWeight: 600, color: "#444" };
const input = {
  padding: 8,
  border: "1px solid #ccc",
  borderRadius: 6,
  width: "100%",
  background: "#fdfdfd",
  color: "#222",
  boxSizing: "border-box",
};
const btnPrimary = {
  padding: "8px 12px",
  borderRadius: 6,
  background: "#d4a017",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
};
const btnGhost = {
  padding: "8px 12px",
  borderRadius: 6,
  background: "#f8f8f8",
  color: "#222",
  border: "1px solid #ccc",
  cursor: "pointer",
};
const btnDanger = {
  padding: "8px 12px",
  borderRadius: 6,
  background: "#c62828",
  color: "#fff",
  border: "none",
  cursor: "pointer",
};
const th = {
  textAlign: "left",
  padding: "10px 8px",
  borderBottom: "1px solid #ddd",
  fontWeight: 700,
  fontSize: 13,
  color: "#d4a017",
  whiteSpace: "nowrap",
};
const td = {
  padding: "10px 8px",
  borderBottom: "1px solid #eee",
  fontSize: 13,
  color: "#333",
};
